import { useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Swal from "sweetalert2";
import { FaStore } from "react-icons/fa";
import {
  FiShield,
  FiLogOut,
  FiCalendar,
  FiChevronDown,
  FiShoppingBag,
  FiUsers,
  FiTruck,
  FiPackage,
  FiFileText,
  FiGrid,
  FiClock,
  FiBarChart2,
  FiMenu,
} from "react-icons/fi";
import noProfile from "assets/images/noprofile.png";

const Header = ({ onToggleSidebar, toggleSidebarCollapse }) => {
  const [isUserDropdownOpen, setUserDropdownOpen] = useState(false);
  const [userName, setUserName] = useState("");
  const [userRole, setUserRole] = useState("");
  const location = useLocation();

  useEffect(() => {
    const nameFromStorage = localStorage.getItem("full_name");
    const roleFromStorage = toCamelCase(localStorage.getItem("role"));

    if (nameFromStorage) setUserName(nameFromStorage);
    if (roleFromStorage)
      setUserRole(
        roleFromStorage.charAt(0).toUpperCase() + roleFromStorage.slice(1)
      );
  }, []);

  const toCamelCase = (str) => {
    if (!str || typeof str !== "string") return "";
    return str
      .toLowerCase()
      .trim()
      .split(/[-_\s]+/)
      .map((word, index) =>
        index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
      )
      .join("");
  };

  const getPageTitle = () => {
    const path = location.pathname;

    if (path === "/") return "Dashboard";
    if (path.startsWith("/transactions")) return "Transaksi Penjualan";
    if (path === "/enterprise/outlet") return "Manajemen Outlet";
    if (path === "/enterprise/createoutlet") return "Tambah Outlet";
    if (path.startsWith("/enterprise/editoutlet")) return "Edit Outlet";

    if (path === "/enterprise/shift") return "Manajemen Shift";
    if (path === "/enterprise/createshift") return "Tambah Shift";
    if (path.startsWith("/enterprise/editshift")) return "Edit Shift";

    if (path === "/enterprise/karyawan") return "Daftar Karyawan";
    if (path === "/enterprise/createkaryawan") return "Tambah Karyawan";
    if (path.startsWith("/enterprise/editkaryawan")) return "Edit Karyawan";
    if (path === "/enterprise/karyawan/shiftingkaryawan") return "Shifting Karyawan";

    if (path === "/enterprise/distributor") return "Distributor";
    if (path === "/enterprise/createdistributor") return "Tambah Distributor";
    if (path.startsWith("/enterprise/editdistributor")) return "Edit Distributor";

    if (path === "/obat-paket/obat") return "Katalog Obat";
    if (path === "/obat-paket/obat/create") return "Tambah Obat Baru";
    if (path.startsWith("/obat-paket/obat/edit")) return "Edit Data Obat";
    if (path.startsWith("/obat-paket/obat/detail")) return "Detail Obat";

    if (path === "/obat-paket/paket") return "Daftar Paket Obat";
    if (path === "/obat-paket/paket/create") return "Buat Paket Obat";

    if (path.startsWith("/inventory/stock-opname")) return "Stock Opname";
    if (path === "/inventory/purchase-order") return "Purchase Order (PO)";
    if (path === "/inventory/invoice") return "Manajemen Invoice";
    if (path.startsWith("/rebalance")) return "Rebalance Stok";
    if (path.startsWith("/logs")) return "Log Aktivitas";
    if (path.startsWith("/report/salesreport")) return "Laporan Penjualan";
    if (path.startsWith("/report/monthlyreport")) return "Laporan Bulanan";

    return "Halaman App";
  };

  const getPageIcon = () => {
    const path = location.pathname;

    if (path === "/") return <FiGrid className="w-5 h-5 text-teal-600" />;
    if (path.startsWith("/transactions")) return <FiShoppingBag className="w-5 h-5 text-emerald-600" />;
    if (path.includes("outlet")) return <FaStore className="w-5 h-5 text-teal-600" />;
    if (path.includes("karyawan") || path.includes("shift")) return <FiUsers className="w-5 h-5 text-teal-600" />;
    if (path.includes("distributor")) return <FiTruck className="w-5 h-5 text-teal-600" />;
    if (path.includes("obat") || path.includes("paket")) return <FiPackage className="w-5 h-5 text-teal-600" />;
    if (path.includes("report")) return <FiBarChart2 className="w-5 h-5 text-teal-600" />;
    if (path.includes("log")) return <FiClock className="w-5 h-5 text-teal-600" />;

    return <FiFileText className="w-5 h-5 text-teal-600" />;
  };

  const currentDateFormatted = new Date().toLocaleDateString("id-ID", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const handleLogout = () => {
    Swal.fire({
      title: "Konfirmasi Logout",
      text: "Apakah Anda yakin ingin keluar dari sistem?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      cancelButtonColor: "#6B7280",
      confirmButtonText: "Ya, Keluar",
      cancelButtonText: "Batal",
      customClass: {
        popup: "rounded-3xl",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        localStorage.clear();
        window.location.href = "/login";
      }
    });
  };

  return (
    <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-gray-100/90 shadow-sm px-6 py-3.5 flex justify-between items-center transition-all duration-300">
      {/* Left: Mobile Toggle + Page Icon & Dynamic Title */}
      <div className="flex items-center gap-3">
        <button
          onClick={onToggleSidebar || toggleSidebarCollapse}
          className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200/80 text-gray-700 lg:hidden transition-colors flex items-center justify-center"
          aria-label="Toggle Sidebar"
        >
          <FiMenu className="w-5 h-5 text-gray-700" />
        </button>

        <div className="p-2.5 rounded-2xl bg-teal-50 text-teal-600 border border-teal-100 shadow-sm flex items-center justify-center">
          {getPageIcon()}
        </div>
        <div>
          <h1 className="text-lg md:text-xl font-extrabold text-gray-900 tracking-tight">
            {getPageTitle()}
          </h1>
          <p className="text-xs text-gray-400 font-medium hidden sm:block">
            Sistem Informasi Apotek Djaya Medika
          </p>
        </div>
      </div>

      {/* Right: Date Badge & User Profile Dropdown */}
      <div className="flex items-center gap-4">
        {/* Date Pill (Desktop) */}
        <div className="hidden lg:flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-gray-50 border border-gray-200/60 text-xs font-semibold text-gray-600">
          <FiCalendar className="w-3.5 h-3.5 text-teal-600" />
          <span>{currentDateFormatted}</span>
        </div>

        {/* User Profile Container */}
        <div className="relative">
          <button
            onClick={() => setUserDropdownOpen(!isUserDropdownOpen)}
            className="flex items-center gap-3 p-1.5 pr-3 rounded-2xl hover:bg-gray-50 transition-all duration-200 border border-transparent hover:border-gray-200/80"
          >
            <div className="relative">
              <img
                src={noProfile}
                alt="avatar"
                className="w-10 h-10 rounded-2xl object-cover ring-2 ring-teal-500/30 shadow-md"
              />
              <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-white" />
            </div>

            <div className="text-left hidden md:block">
              <p className="text-xs font-bold text-gray-900 leading-tight">
                {userName || localStorage.getItem("full_name") || "User Apotek"}
              </p>
              <span className="inline-block text-[10px] font-extrabold uppercase px-1.5 py-0.5 rounded-md bg-teal-50 text-teal-700 mt-0.5">
                {userRole || localStorage.getItem("role") || "Pengguna"}
              </span>
            </div>

            <FiChevronDown
              className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                isUserDropdownOpen ? "rotate-180 text-teal-600" : ""
              }`}
            />
          </button>

          {/* User Dropdown Menu */}
          <AnimatePresence>
            {isUserDropdownOpen && (
              <motion.div
                initial={{ opacity: 0, y: 8, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="absolute right-0 mt-2 w-56 bg-white rounded-3xl shadow-2xl shadow-gray-200/80 border border-gray-100 p-2 z-50 overflow-hidden"
              >
                {/* Header Profile Inside Dropdown */}
                <div className="p-3 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-2xl mb-1 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-teal-600 text-white font-bold flex items-center justify-center text-sm shadow-md">
                    {(userName || "U").charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-extrabold text-gray-900 truncate">
                      {userName || "User"}
                    </p>
                    <p className="text-[10px] text-gray-500 font-medium">
                      Status: Online
                    </p>
                  </div>
                </div>

                <div className="space-y-0.5 text-xs font-semibold">
                  <button className="flex items-center gap-2.5 w-full px-3.5 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-teal-600 rounded-xl transition-colors">
                    <FiShield className="w-4 h-4 text-teal-600" />
                    <span>Profil Pengguna</span>
                  </button>

                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2.5 w-full px-3.5 py-2.5 text-rose-600 hover:bg-rose-50 rounded-xl transition-colors"
                  >
                    <FiLogOut className="w-4 h-4 text-rose-600" />
                    <span>Keluar (Logout)</span>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </header>
  );
};

export default Header;
